import { useFeatureIsOn } from '@growthbook/growthbook-react';
import { Grid } from '@mui/material';
import CallToSignupMiniBanner from 'components/CallToSignupMiniBanner/CallToSignupMiniBanner';
import ChipsItem from 'components/ChipsItem';
import BlockShop from 'components/layout/BlockShop/BlockShop';
import PaymentShield from 'components/PaymentShield/PaymentShield';
import PaymentShieldHighlighted from 'components/PaymentShieldHighlighted/PaymentShieldHighlighted';
import { RatingFeedbacks } from 'components/Rating/Rating';
import SocialToolbar from 'components/SocialToolbar/SocialToolbar';
import { BRAND, CATEGORY_PRODUCTS, SEARCH, SHOP } from 'config/fe-routes';
import { useRouter } from 'next/router';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import IosInformationCircleOutline from 'react-ionicons/lib/IosInformationCircleOutline';
import { connect } from 'react-redux';
import buildFormatter from 'react-timeago/lib/formatters/buildFormatter';
import shortTimeFormatter from 'react-timeago/lib/language-strings/en-short';
import { ProductCondition, ProductType } from 'types/product.types';
import { UserType } from 'types/user.types';
import { toKebabCase } from 'utils/format-text';
import shortTimeFormatterEstonian from 'utils/time-ago.et-short';
import { categoryNameToUrl } from 'utils/url';

import config, { Language } from '../../../config';
import { useLanguage } from '../../../hooks/useLanguage';
import Tooltip from '../../Tooltip/Tooltip';
import OwnerActions from '../OwnerActions/OwnerActions';
import ProductActions from '../ProductActions/ProductActions';
import ProductMetaInfo from '../ProductMetaInfo/ProductMetaInfo';
// Import components for refactoring
import ProductPrice from '../ProductPrice/ProductPrice';

type ProductDetailsProps = {
  product: ProductType;
  availableConditions?: ProductCondition[];
  likeCallback?: () => void;
  likeCount?: number;
  onMarkSold: () => void;
  onVisibilityChange: () => void;
  onDelete: () => void;
  onEdit: () => void;
  onLikeToggle?: (liked: boolean) => void;
  user: UserType;
  refreshParentDataFn?: () => void;
};

const mapStateToProps = ({ userData }) => ({
  user: userData && userData.user,
});

const ProductDetails = (props: ProductDetailsProps) => {
  const {
    product,
    likeCallback,
    likeCount,
    onMarkSold,
    onDelete,
    onEdit,
    onVisibilityChange,
    onLikeToggle,
    user,
    availableConditions,
    refreshParentDataFn,
  } = props;
  const { t } = useTranslation();
  const { query } = useRouter();
  const { language } = useLanguage();

  const shopSlug = query['shop-slug']?.toString();
  const productSlug = query['product-slug']?.toString();

  const [descriptionExpanded, toggleDescription] = useState(false);
  const [showOriginalDescription, setShowOriginalDescription] = useState(false);
  const [visibleDescription, setVisibleDescription] = useState('');
  // eslint-disable-next-line no-unused-vars
  const [brandLink, setBrandLink] = useState<any>();

  const formatter = buildFormatter(language === Language.et ? shortTimeFormatterEstonian : shortTimeFormatter);

  const isProductDeleted = product?.status === 'deleted';
  const isProductEraser = +product?.deleted_by === +user?.id;
  const buyerProtectionHighlighted = config.get('buyerProtectionHighlighted');
  const likedProducts = user?.likedProducts || {};

  const shopFeedbacks: RatingFeedbacks = { avgRating: product.shop.avgRating ?? 0, total: product.shop.feedbackTotal ?? 0 };

  // trigger the experiment exposure event even though the logic is handled in the backend
  useFeatureIsOn('bpf_override');

  const getConditionsTooltipContent = (product?: ProductType): string => {
    return availableConditions
      ?.map((condition) => {
        let conditionName = condition.name;

        if (product.condition?.name?.toLowerCase() === conditionName.toLowerCase()) {
          conditionName = `<span>${conditionName}</span>`;
        }

        return `${conditionName}<br><span class="text-grey">${condition.description}</span>`;
      })
      .join('<br><hr>');
  };

  useEffect(() => {
    if (showOriginalDescription) {
      setVisibleDescription(product.description);
    } else {
      setVisibleDescription(product.translatedDescription);
    }
  }, [showOriginalDescription]);

  useEffect(() => {
    setVisibleDescription(product.translatedDescription || product.description);
    setShowOriginalDescription(product.translatedDescription ? false : true);

    if (product?.brand) {
      setBrandLink(BRAND(toKebabCase(product?.brand)));
    }
  }, [product]);

  return (
    <Grid container item md={6} sm={12} className="meta-info">
      {/* SOLD STATUS */}
      {product.status === 'sold' && (
        <Grid item xs={12} className="sold-container">
          <h4>{t('components.page-product.product-details.sold')}</h4>
        </Grid>
      )}

      <Grid container alignItems="center">
        {/* PRICE AND DELIVERY INFORMATION */}
        <ProductPrice
          product={product}
          buyerProtectionHighlighted={buyerProtectionHighlighted}
          handleBundlingTimeEnd={refreshParentDataFn}
        />
      </Grid>

      {/* BUYER ACTION BUTTONS */}
      <ProductActions
        product={product}
        isOwner={product.isOwner}
        buyerProtectionHighlighted={buyerProtectionHighlighted}
        offerEnabled={config.offer.enabled}
        refreshParentDataFn={refreshParentDataFn}
      />

      {/* OWNER ACTIONS */}
      <OwnerActions
        isOwner={product.isOwner}
        isProductDeleted={isProductDeleted}
        isProductEraser={isProductEraser}
        status={product.status}
        onEdit={onEdit}
        onMarkSold={onMarkSold}
        onVisibilityChange={onVisibilityChange}
        onDelete={onDelete}
        isDeleteDisabled={product.orderInProgress}
      />

      {/* LIKES, CREATED AT, LOCATION row */}
      <ProductMetaInfo
        product={product}
        user={user}
        likeCount={likeCount || 0}
        isProductDeleted={isProductDeleted}
        formatter={formatter}
        productSlug={productSlug}
        onLikeToggle={onLikeToggle}
      />

      {/* DESCRIPTION */}
      <Grid container className="description">
        {visibleDescription && (
          <>
            {visibleDescription.length > 200 && !descriptionExpanded && (
              <p>
                {visibleDescription.substring(0, 200)}...
                <span className="read-more" role="link" tabIndex={0} onClick={() => toggleDescription(true)}>
                  {t('components.page-product.product-details.keep-reading')}
                </span>
              </p>
            )}

            {(visibleDescription.length <= 200 || descriptionExpanded) && <p>{visibleDescription}</p>}
          </>
        )}
      </Grid>

      {/* Translation notice if product description has been auto translated  */}
      {product.translatedDescription && !showOriginalDescription && (
        <Grid container className="description-translation-notice">
          <p role="link" tabIndex={1} onClick={() => setShowOriginalDescription(true)}>
            {t('components.page-product.product-details.description-show-original')}
          </p>
        </Grid>
      )}

      {product.translatedDescription && showOriginalDescription && (
        <Grid container className="description-translation-notice">
          <p role="link" tabIndex={1} onClick={() => setShowOriginalDescription(false)}>
            {t('components.page-product.product-details.description-show-translated')}
          </p>
        </Grid>
      )}

      {/* Product extra info container */}
      <Grid container className="product-info-table">
        {/* PRODUCT SIZE */}
        {product?.size && (
          <ChipsItem
            title={`${t('components.page-product.product-details.size')}: ${product?.size}`}
            isClickable={false}
            clickableLink={null}
          />
        )}

        {/* BRAND */}
        {product?.brand && <ChipsItem title={product.brand} isClickable={true} clickableLink={brandLink} />}

        {/* COLOR */}
        {!!product?.colors?.length &&
          product.colors.map((color) => <ChipsItem key={color.id} title={color.name} isClickable={false} clickableLink={null} />)}

        {/* CONDITION */}
        {!!product?.condition?.name && (
          <>
            <div className="product-condition">
              <Tooltip
                title={getConditionsTooltipContent(product)}
                tooltipContentClassName={'product-conditions-tooltip'}
                disableTitleTranslation={true}
                iconClassName="condition-info-icon"
                place="top"
              >
                <IosInformationCircleOutline className="condition-Tooltip" />

                <ChipsItem
                  title={`${t('components.page-product.product-details.condition')}: ${product?.condition?.name}`}
                  isClickable={false}
                  clickableLink={null}
                />
              </Tooltip>
            </div>
          </>
        )}

        {/* CATEGORY */}
        {!!product?.categories?.length &&
          product.categories.map((category) => (
            <ChipsItem
              key={category.id}
              title={category.title}
              isClickable={true}
              clickableLink={{ ...CATEGORY_PRODUCTS(categoryNameToUrl(category.title)) }}
            />
          ))}

        {/* MATERIALS */}
        {!!product?.materials?.length &&
          product.materials.map((material) => (
            <ChipsItem key={material.id} title={material.name} isClickable={false} clickableLink={null} />
          ))}
      </Grid>

      {/* Product AI tags, visible for admins only */}
      {user?.is_admin && (
        <Grid container className="product-ai-tags">
          {/* AI TAGS */}
          {!!product?.aiTags?.length &&
            product?.aiTags
              .split(',')
              .map((tag) => <ChipsItem key={tag} title={tag} isClickable={true} clickableLink={{ ...SEARCH(tag) }} />)}
        </Grid>
      )}

      {/* PROTECTED MESSAGE */}
      {!product.isOwner &&
        (buyerProtectionHighlighted ? (
          <PaymentShieldHighlighted
            buyerSideFeesPercentage={product.buyerSideFeesPercentage}
            buyerSideFeesFixed={product.buyerSideFeesFixed}
          />
        ) : (
          <PaymentShield
            buyerProtectionFeeEnabled={product.buyerProtectionFeeEnabled}
            buyerSideFeesPercentage={product.buyerSideFeesPercentage}
            buyerSideFeesFixed={product.buyerSideFeesFixed}
          />
        ))}

      {/* SOCIAL MEDIA */}
      {!isProductDeleted && !product?.shop?.isBlocked && (
        <Grid container className="social-container mt-2">
          <SocialToolbar
            likeCallback={likeCallback}
            isLiked={likedProducts && likedProducts[product?.id]}
            shopSlug={shopSlug}
            productSlug={product.slug}
            hideLike={product.isOwner}
            hideMessage={product.isOwner}
          />
        </Grid>
      )}

      {/* SHOP INFO */}
      <BlockShop
        name={product?.shop?.name}
        slug={product?.shop?.active_slug}
        showSlug={false}
        showActions={user?.isLoggedIn}
        product={product}
        user={user}
        actionsList={[{ name: t('components.report.title'), actionType: 'report' }]}
        lastActive={product?.user?.lastActive}
        imageSrc={product?.user?.profilePicture?.original || product?.user?.picture}
        {...SHOP(product?.shop?.active_slug)}
        className="pb-0-i my-2-i product-page-shop"
        imageSize={50}
        refreshParentDataFn={refreshParentDataFn}
        orderCount={product?.shop?.completedOrdersCount}
        likeCount={product?.shop?.likeCount}
        formattedOrderProcessingTime={product?.shop?.formattedOrderProcessingTime}
        feedbacks={shopFeedbacks}
      />

      {/* CALL TO SIGNUP, MINI BANNER */}
      {!user?.isLoggedIn && product.status !== 'sold' && (
        <CallToSignupMiniBanner likeCallback={likeCallback} className="productdetails-calltosignupminibanner" />
      )}
    </Grid>
  );
};

export default connect(mapStateToProps)(ProductDetails);
